import { useEffect, useState } from 'react';
import {
  DialOption as DialOptionType,
  RouteType,
} from '@weave/schema-gen-ts/dist/schemas/phone/tree/v1/phone_tree_service.pb';
import { useQuery } from 'react-query';
import PhoneTreeAPI from '@frontend/api-phone-trees';
import { useTranslation } from '@frontend/i18n';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { findMediaItem } from '../../components/audio-picker/audio-picker';
import { useCallObjectsData } from '../../components/call-routes/use-call-objects-data';
import { DialOptionData } from '../../components/phone-tree-quick-actions/types';
import { queryKeys } from '../../query-keys';
import { getNameWithoutExtension } from '../../utils/phone-utils';

export const usePhoneTreeData = ({
  phoneTreeId,
  phoneTreeQueryOptions,
  callObjectsQueryOptions,
}: {
  phoneTreeId: string;
  phoneTreeQueryOptions?: {
    staleTime?: number;
    cacheTime?: number;
    refetchOnMount?: boolean | 'always';
  };
  callObjectsQueryOptions?: {
    staleTime?: number;
    cacheTime?: number;
  };
}) => {
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const { t } = useTranslation('phone');

  const [transformedOptions, setTransformedOptions] = useState<DialOptionData[]>([]);

  const { data: phoneTreeData, isLoading: phoneTreeIsLoading } = useQuery({
    queryKey: [settingsTenantLocation?.phoneTenantId, ...queryKeys.settings.readPhoneTree(phoneTreeId)],
    queryFn: () => PhoneTreeAPI.Read({ phoneTreeId }),
    enabled: !!settingsTenantLocation?.phoneTenantId && !!phoneTreeId,
    staleTime: phoneTreeQueryOptions?.staleTime ?? 1 * 60 * 1000,
    cacheTime: phoneTreeQueryOptions?.cacheTime ?? 1 * 60 * 1000,
    refetchOnMount: phoneTreeQueryOptions?.refetchOnMount || false,
    meta: {
      error: {
        message: 'usePhoneTreeData:phoneTreeQuery | Failed to fetch phone tree data',
        data: { phoneTreeId },
      },
    },
  });

  const {
    callGroups,
    callQueues,
    phoneTrees,
    forwardingNumbers,
    voicemailBoxes,
    devices,
    someIsLoading,
    routes,
    phoneHours,
    audioFiles,
  } = useCallObjectsData(settingsTenantLocation, callObjectsQueryOptions);

  const callObjectsLoading = someIsLoading;
  const isLoading = phoneTreeIsLoading || callObjectsLoading;

  useEffect(() => {
    if (isLoading || !phoneTreeData) {
      return;
    }

    const transformed = transformDialOptions(phoneTreeData.dialOptions);

    setTransformedOptions(transformed);
  }, [
    phoneTreeData,
    callGroups,
    callQueues,
    phoneTrees,
    forwardingNumbers,
    voicemailBoxes,
    devices,
    isLoading,
    routes,
    audioFiles,
    phoneHours,
    t,
  ]);

  const transformDialOptions = (dialOptions: DialOptionType[]): DialOptionData[] => {
    // Here we transform options if needed using the fetched dial options data
    const transformed = dialOptions.map((option) => {
      // For each dial option, we could enrich the instruction routes with names or details from the fetched data
      const enrichedRoutes: DialOptionData['routes'] = [];
      let shouldHideSubsequentRoutes = false;

      for (const route of option.routes) {
        switch (route.type) {
          case RouteType.ROUTE_TYPE_CALL_GROUP: {
            const callGroup = callGroups.find((cg) => cg.id === route.payload?.callGroupId);
            enrichedRoutes.push({
              type: RouteType.ROUTE_TYPE_CALL_GROUP,
              id: callGroup?.id || '',
              name: callGroup?.name || 'Unknown Call Group',
              shouldHide: shouldHideSubsequentRoutes,
              originalPayload: { ...route.payload },
            });

            break;
          }
          case RouteType.ROUTE_TYPE_CALL_QUEUE: {
            const callQueue = callQueues.find((cq) => cq.callQueueId === route.payload?.callQueueId);
            enrichedRoutes.push({
              type: route.type,
              id: callQueue?.callQueueId || '',
              name: callQueue?.name || 'Unknown Call Queue',
              shouldHide: shouldHideSubsequentRoutes,
              originalPayload: { ...route.payload },
            });

            break;
          }
          case RouteType.ROUTE_TYPE_IVR_MENU: {
            const phoneTree = phoneTrees.find((pt) => pt.id === route.payload?.subTreeId);
            enrichedRoutes.push({
              type: route.type,
              id: phoneTree?.id || '',
              name: phoneTree?.name || 'Unknown Phone Tree',
              shouldHide: shouldHideSubsequentRoutes,
              originalPayload: { ...route.payload },
            });

            shouldHideSubsequentRoutes = true; // IVR Menu is a terminal instruction
            break;
          }
          case RouteType.ROUTE_TYPE_REPEAT: {
            // If the route.payload.subTreeId is set, then this is a repeat of phone tree different than the current one.
            const phoneTree = phoneTrees.find((pt) => pt.id === route.payload?.subTreeId);

            enrichedRoutes.push({
              type: route.type,
              id: 'repeat',
              name: t('Repeat Menu'),
              shouldHide: shouldHideSubsequentRoutes,
              originalPayload: { ...route.payload },
              data: {
                treeId: phoneTree?.id || '',
                treeName: phoneTree?.name || 'Unknown Phone Tree',
              },
            });
            break;
          }
          case RouteType.ROUTE_TYPE_FORWARDING_NUMBER: {
            const forwardingNumber = forwardingNumbers.find((fn) => fn.id === route.payload?.forwardingNumberId);
            enrichedRoutes.push({
              type: route.type,
              id: forwardingNumber?.id || '',
              name: forwardingNumber
                ? `${forwardingNumber.name} (${forwardingNumber.number})`
                : 'Unknown Forwarding Number',
              shouldHide: shouldHideSubsequentRoutes,
              originalPayload: { ...route.payload },
            });

            break;
          }
          case RouteType.ROUTE_TYPE_CALL_ROUTE: {
            const callRoute = routes.find((cr) => cr.callRouteId === route.payload?.callRouteId);
            enrichedRoutes.push({
              type: route.type,
              id: callRoute?.callRouteId || '',
              name: callRoute?.name || 'Unknown Call Route',
              shouldHide: shouldHideSubsequentRoutes,
              originalPayload: { ...route.payload },
            });

            break;
          }
          case RouteType.ROUTE_TYPE_DEVICE: {
            const device = devices.find((d) => d.deviceId === route.payload?.deviceId);
            enrichedRoutes.push({
              type: route.type,
              id: device?.deviceId || '',
              name: device?.name || 'Unknown Device',
              shouldHide: shouldHideSubsequentRoutes,
              originalPayload: { ...route.payload },
            });

            break;
          }
          case RouteType.ROUTE_TYPE_PLAY_MESSAGE: {
            const playMessagePayload = route.payload;

            const audioItem = findMediaItem(playMessagePayload?.greetingId ?? '', audioFiles?.allMedia ?? []);

            enrichedRoutes.push({
              type: route.type,
              id: audioItem?.id || '',
              name: getNameWithoutExtension(audioItem?.name) || 'Unknown Play Message',
              shouldHide: shouldHideSubsequentRoutes,
              originalPayload: { ...route.payload },
              data: {
                filePath: audioItem?.path || '',
              },
            });

            break;
          }
          case RouteType.ROUTE_TYPE_PHONE_HOURS: {
            const phoneHoursPayload = route.payload;
            const phoneHoursItem = phoneHours.find((ph) => ph.id === phoneHoursPayload?.phoneHoursId);
            enrichedRoutes.push({
              type: route.type,
              id: phoneHoursItem?.id || '',
              name: phoneHoursItem?.name || 'Unknown Phone Hours',
              shouldHide: shouldHideSubsequentRoutes,
              originalPayload: { ...route.payload },
            });

            break;
          }
          case RouteType.ROUTE_TYPE_VOICEMAIL: {
            const voicemailPayload = route.payload;
            const voicemailBox = voicemailBoxes?.find((vb) => vb.mailbox.id === voicemailPayload?.voicemailBoxId);

            enrichedRoutes.push({
              type: route.type,
              id: voicemailBox?.mailbox.id || '',
              name: voicemailBox?.mailbox.name || 'Unknown Voicemail Box',
              shouldHide: shouldHideSubsequentRoutes,
              originalPayload: { ...route.payload },
              data: {
                voicemailBoxId: voicemailBox?.mailbox.id || '',
                voicemailGreetingId: voicemailPayload?.voicemailGreetingId,
                systemMediaId: voicemailPayload?.systemMediaId,
                skipGreeting: voicemailPayload?.skipGreeting,
              },
            });

            break;
          }
          default:
            enrichedRoutes.push({
              type: route.type,
              id: '',
              name: '',
              shouldHide: shouldHideSubsequentRoutes,
              originalPayload: { ...route.payload },
            });
        }
      }

      return {
        number: option.number,
        routes: enrichedRoutes,
      };
    });

    return transformed;
  };

  return { phoneTreeData, phoneTreeIsLoading, callObjectsLoading, transformedOptions, transformDialOptions };
};
